// Feature #313 — Capability Catalogue admin (sm-admin).
//
// Create capabilities, describe them, deprecate and restore.
//
// Restore is present deliberately. Bug #310 was hard to resolve precisely
// because the skills catalogue's Deprecate is one-way with no way back in
// the UI, leaving a row that could not be managed without API access. That
// mistake is not repeated here.
//
// Feature #356 — a capability badge is assigned by a Skills Manager, not
// earned from ratings, so the minimums editor, the requirement digest and
// the pips that #321 added are gone from this screen. The requirement sets
// still exist server-side (retained inert, same rule as #355's core
// competencies) but nothing reads them and an admin has nothing to edit
// here beyond the name, the description and whether it is live. sm_admin
// defines the catalogue and has no standing on any individual: assigning
// badges is the Skills Manager's, on Staff Detail.
import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  createCapability,
  getCapabilities,
  updateCapability,
  SmCapabilityListEntry,
} from '@/api/sm'
import { blockStyle } from '../components/SmShared'

const primaryButton: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryButton: React.CSSProperties = {
  padding: '6px 12px',
  backgroundColor: 'transparent',
  color: 'var(--color-magenta)',
  border: '1px solid var(--color-magenta)',
  borderRadius: '6px',
  fontSize: '13px',
  minHeight: 'auto',
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const CapabilityCatalogue: React.FC = () => {
  const [items, setItems] = useState<SmCapabilityListEntry[]>([])
  const [showInactive, setShowInactive] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    getCapabilities({ includeInactive: showInactive })
      .then((d) => setItems(d.items))
      .catch((e) => setError(e?.response?.data?.detail || e?.message))
  }

  useEffect(load, [showInactive])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try {
      await fn()
      load()
    } catch (e) {
      // Narrowed rather than typed `any`: the API's 409 on a duplicate name
      // arrives as `detail`, and losing it to a generic message would make
      // that state unexplainable.
      const err = e as { response?: { data?: { detail?: string } }; message?: string }
      setError(err?.response?.data?.detail || err?.message || 'Action failed')
    }
  }

  return (
    <div>
      <PageHeader title="Capability Catalogue" />
      <div style={{ padding: '24px', display: 'grid', gap: '24px' }}>
        {error && <div style={{ color: '#b91c1c' }}>{error}</div>}

        <section style={blockStyle}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
            New capability
          </h2>
          <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: '0 0 14px' }}>
            A capability is a named thing The Lab can deliver. Once it exists, a Skills
            Manager assigns its badge to people from their skills profile.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name"
              style={{ flex: '1 1 220px', fontSize: '14px' }}
            />
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description (optional)"
              style={{ flex: '2 1 320px', fontSize: '14px' }}
            />
            <button
              type="button"
              style={primaryButton}
              disabled={!newName.trim()}
              onClick={() =>
                run(async () => {
                  await createCapability({
                    name: newName.trim(),
                    description: newDescription.trim() || undefined,
                  })
                  setNewName('')
                  setNewDescription('')
                })
              }
            >
              Create
            </button>
          </div>
          {/* Bug #316: say why the button is inert rather than leaving the
              reader to guess. Description alone is not enough to create. */}
          {!newName.trim() && (
            <p
              style={{
                fontSize: '12px',
                color: 'var(--color-dark-grey)',
                margin: '10px 0 0',
              }}
            >
              Enter a name to create a capability.
            </p>
          )}
        </section>

        <section style={blockStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              marginBottom: '14px',
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Capabilities</h2>
            <label style={{ fontSize: '13px', color: 'var(--color-dark-grey)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ marginRight: '6px' }}
              />
              Show deprecated
            </label>
          </div>

          {items.length === 0 && (
            <p style={{ color: 'var(--color-dark-grey)', margin: 0 }}>
              No capabilities defined yet.
            </p>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {items.map((capability) => (
              <div
                key={capability.capability_id}
                style={{
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  opacity: capability.is_active ? 1 : 0.6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    {capability.name}
                    {!capability.is_active && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#92400e' }}>
                        deprecated
                      </span>
                    )}
                  </div>
                  {capability.description && (
                    <div
                      style={{
                        color: 'var(--color-dark-grey)',
                        fontSize: '13px',
                        marginTop: '2px',
                      }}
                    >
                      {capability.description}
                    </div>
                  )}
                  {!capability.is_active && (
                    <div style={{ color: '#92400e', fontSize: '12px', marginTop: '6px' }}>
                      Badges already assigned are hidden while it is deprecated and come back
                      on restore — nobody needs re-assigning.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <button
                    type="button"
                    style={secondaryButton}
                    onClick={() =>
                      run(() =>
                        updateCapability(capability.capability_id, {
                          is_active: !capability.is_active,
                        })
                      )
                    }
                  >
                    {capability.is_active ? 'Deprecate' : 'Restore'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

export default CapabilityCatalogue
